import { emailEscape } from './brandedEmail';
import { pruebaTerminadaSinPlan, zonaDeLaPrueba } from './finPrueba';
import { founderTrialActive } from './founderTrial';
import { HELP_VIDEO_BASE } from './help';
import type { Membership, Tenant } from './types';
import { weeklyLocalDate } from './weeklyReport';

/**
 * «Te activamos 14 días más en Alexia» (Alex, 21 oct 2026): a quien probó
 * Alexia y nunca pagó se le abren 14 días más de prueba (la prueba es Pro,
 * ver plans.ts) y se le manda un correo con el vídeo de cómo es ahora.
 *
 * Lo envía `infra/scripts/reactivacion.mts`, que primero da los 14 días a
 * cada cuenta y solo después manda el correo: si alguien entra y le piden
 * pagar, lo perdemos.
 *
 * Dos versiones (Pol, 21 oct 2026). La frase del 50 % los 3 primeros meses
 * convierte el correo en promocional (shared/correos.ts): solo la lleva quien
 * marcó la casilla de newsletter. El resto recibe el mismo correo sin esa
 * frase, como correo de cuenta. El cupón de lanzamiento se aplica igual en el
 * checkout mensual para todos (billing.ts).
 */

export const REACTIVACION_ID = 'reactivacion-2026-10';
export const REACTIVACION_DIAS = 14;
export const ASUNTO_REACTIVACION = 'Te activamos 14 días más en Alexia';
export const PREHEADER_REACTIVACION = 'Alexia cambió mucho y queremos que la veas con tus números.';
export const REMITENTE_REACTIVACION = 'Alex de Alexia';

/** Vídeo y miniatura en el AyudaBucket (infra/scripts/reactivacion-medios.sh). */
export const REACTIVACION_VIDEO_URL = `${HELP_VIDEO_BASE}/reactivacion/alexia-ahora-2026-10.mp4`;
export const REACTIVACION_MINIATURA_URL = `${HELP_VIDEO_BASE}/reactivacion/miniatura-video.jpg`;

const PROD = 'https://app.alexiarest.com';
const DIA = 86_400_000;

export type MotivoReactivacion = 'prueba' | 'founder';

/**
 * ¿Le toca? Cuentas que probaron (o reservaron Founder) y nunca pagaron, con
 * la prueba ya terminada. Fuera: internas, demo, marcadas para borrar,
 * cortesía, quien pagó alguna vez (su caso es la baja o el impago) y quien
 * sigue en prueba.
 */
export function motivoReactivacion(tenant: Tenant & { isDemo?: boolean }, ahora: number = Date.now()): MotivoReactivacion | null {
  if (!tenant?.tenantId || tenant.internal || tenant.isDemo || tenant.tenantId.startsWith('demo') || tenant.deletion) return null;
  if (tenant.plan !== 'trial' || tenant.subscriptionStatus !== 'trialing') return null;
  if (tenant.firstPaidAt || tenant.stripeSubscriptionId || tenant.commercialEvents?.some((e) => e.type === 'alta')) return null;
  if (tenant.founderPending) return founderTrialActive(tenant, ahora) ? null : 'founder';
  return pruebaTerminadaSinPlan(tenant, ahora) ? 'prueba' : null;
}

function sumarDias(fecha: string, dias: number): string {
  return new Date(Date.parse(`${fecha}T00:00:00Z`) + dias * DIA).toISOString().slice(0, 10);
}

export interface ConcesionReactivacion {
  motivo: MotivoReactivacion;
  /** Nuevo `trialEndsAt`: un día (prueba normal) o un instante (Founder pendiente). */
  trialEndsAt: string;
  /** Solo Founder pendiente: el nuevo `founderTrial`. */
  founderTrial?: { startedAt: string; endsAt: string };
  /** Lo que se guarda en el tenant (`Tenant.reactivacion`). */
  registro: NonNullable<Tenant['reactivacion']>;
}

/**
 * Los 14 días que se le dan. La prueba normal termina a las 00:00 del día
 * `trialEndsAt` en la hora del restaurante (finDeLaPrueba): hoy + 14 son 14
 * días contando hoy. El Founder pendiente usa `founderTrial`, con hora.
 *
 * Si tuvo la extensión de 7 días, se guarda en `anterior` y se quita del
 * tenant: si no, la extensión volvería a estar «activa» y le pondría su tope
 * de 10 escaneos y 10 dictados para siempre (trialExtensionPolicy.ts).
 */
export function concesionReactivacion(tenant: Tenant, ahora: Date = new Date()): ConcesionReactivacion | null {
  const motivo = motivoReactivacion(tenant, ahora.getTime());
  if (!motivo) return null;
  const grantedAt = ahora.toISOString();
  const anterior = {
    ...(tenant.trialEndsAt ? { trialEndsAt: tenant.trialEndsAt } : {}),
    ...(tenant.trialExtension ? { trialExtension: tenant.trialExtension } : {}),
    ...(tenant.founderTrial ? { founderTrial: tenant.founderTrial } : {}),
  };
  if (motivo === 'founder') {
    const endsAt = new Date(ahora.getTime() + REACTIVACION_DIAS * DIA).toISOString();
    return {
      motivo,
      trialEndsAt: endsAt,
      founderTrial: { startedAt: grantedAt, endsAt },
      registro: { id: REACTIVACION_ID, motivo, grantedAt, endsAt, anterior },
    };
  }
  const hoy = weeklyLocalDate(ahora, zonaDeLaPrueba(tenant));
  const endsAt = sumarDias(hoy, REACTIVACION_DIAS);
  return { motivo, trialEndsAt: endsAt, registro: { id: REACTIVACION_ID, motivo, grantedAt, endsAt, anterior } };
}

export interface DestinatarioReactivacion {
  email: string;
  nombre?: string;
  tenantIds: string[];
  /** Marcó la newsletter en alguna de sus cuentas: versión con el 50 %. */
  promocional: boolean;
}

/**
 * A quién se escribe: los propietarios activos de las cuentas que tocan, una
 * vez por email. Quien apagó en Ajustes la guía y la oferta de días extra
 * (`WELCOMEPREF#<userId>` con `enabled: false`) no lo recibe: es la misma
 * clase de correo.
 */
export function elegirDestinatariosReactivacion(
  tenants: Tenant[],
  memberships: Membership[],
  opciones: { ahora?: number; apagados?: Set<string> } = {},
): DestinatarioReactivacion[] {
  const ahora = opciones.ahora ?? Date.now();
  const apagados = opciones.apagados ?? new Set<string>();
  const validos = new Map(tenants.filter((t) => motivoReactivacion(t, ahora)).map((t) => [t.tenantId, t]));
  const porEmail = new Map<string, DestinatarioReactivacion>();
  const ordenadas = [...memberships].sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));
  for (const m of ordenadas) {
    const tenant = validos.get(m.tenantId);
    if (!tenant) continue;
    if (m.role !== 'owner' || m.customRoleId || (m.status ?? 'active') !== 'active') continue;
    if (apagados.has(`${m.tenantId}#${m.userId}`)) continue;
    const email = (m.email ?? '').trim().toLowerCase();
    if (!email || !email.includes('@')) continue;
    const nombre = m.name?.trim().split(/\s+/)[0] || undefined;
    const promo = Boolean(tenant.newsletterConsentAt);
    const ya = porEmail.get(email);
    if (ya) {
      if (!ya.tenantIds.includes(m.tenantId)) ya.tenantIds.push(m.tenantId);
      if (!ya.nombre && nombre) ya.nombre = nombre;
      ya.promocional ||= promo;
      continue;
    }
    porEmail.set(email, { email, ...(nombre ? { nombre } : {}), tenantIds: [m.tenantId], promocional: promo });
  }
  return [...porEmail.values()].sort((a, b) => a.email.localeCompare(b.email));
}

export interface EnlacesCorreoReactivacion {
  appUrl?: string;
  /** YouTube «no listado» o el mp4 del AyudaBucket (por defecto). */
  videoUrl?: string;
  miniaturaUrl?: string;
  logoUrl?: string;
  /** Adónde va «Darme de baja» en la versión promocional (mailto). */
  bajaUrl?: string;
}

/** «Hola, Carlos:» o, sin nombre, «Hola:». */
export function saludoReactivacion(nombre: string | undefined): string {
  const n = (nombre ?? '').trim().replace(/\s+/g, ' ');
  return n ? `Hola, ${n}:` : 'Hola:';
}

/** El correo de Alex (correo-reactivacion.html) con los huecos rellenos. */
export function correoReactivacion(
  nombre: string | undefined,
  opciones: { promocional: boolean } & EnlacesCorreoReactivacion,
): { asunto: string; preheader: string; html: string; texto: string } {
  const e = emailEscape;
  const app = (opciones.appUrl ?? PROD).replace(/\/$/, '');
  const video = opciones.videoUrl ?? REACTIVACION_VIDEO_URL;
  const miniatura = opciones.miniaturaUrl ?? REACTIVACION_MINIATURA_URL;
  const logo = opciones.logoUrl ?? `${PROD}/logo/logotipo-pdf.png`;
  const gestionar = `${app}/ajustes?tab=cuenta`;
  const saludo = saludoReactivacion(nombre);
  const noticia = opciones.promocional
    ? 'ahora el plan Básico ya incluye todo esto, con 150 escaneos de facturas al mes. Y si decides quedarte, los 3 primeros meses pagas la mitad.'
    : 'ahora el plan Básico ya incluye todo esto, con 150 escaneos de facturas al mes.';
  const pie = opciones.promocional && opciones.bajaUrl
    ? `<a href="${e(opciones.bajaUrl)}" style="color:#91908C">Darme de baja</a>`
    : `<a href="${e(gestionar)}" style="color:#91908C">Gestionar correos</a>`;
  const p = (texto: string, abajo = 14) => `<p style="margin:0 0 ${abajo}px 0;font-size:16px;line-height:1.6;color:#37352F">${texto}</p>`;

  const html = `<!doctype html>
<html lang="es" xmlns="http://www.w3.org/1999/xhtml">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="X-UA-Compatible" content="IE=edge">
<meta name="x-apple-disable-message-reformatting">
<title>${e(ASUNTO_REACTIVACION)}</title>
<style>
  body{margin:0;padding:0;background:#FBF7F1}
  a{color:#14776A}
  @media (max-width:620px){
    .wrap{width:100% !important}
    .pad{padding-left:22px !important;padding-right:22px !important}
    .h1{font-size:26px !important}
  }
</style>
</head>
<body style="margin:0;padding:0;background:#FBF7F1">
<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:#FBF7F1">${e(PREHEADER_REACTIVACION)}&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;&#847;&zwnj;&nbsp;</div>

<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#FBF7F1">
<tr><td align="center" style="padding:28px 12px">

  <table role="presentation" class="wrap" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;background:#FFFFFF;border-radius:16px;overflow:hidden">

    <tr><td class="pad" style="padding:30px 40px 6px 40px">
      <img src="${e(logo)}" width="150" alt="alexia rest." style="display:block;width:150px;height:auto;border:0">
    </td></tr>

    <tr><td class="pad" style="padding:18px 40px 0 40px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif;color:#1B1A17">
      <h1 class="h1" style="margin:0 0 16px 0;font-size:30px;line-height:1.15;font-weight:700;letter-spacing:-0.5px;color:#1B1A17">Te activamos 14 días más en Alexia.</h1>
      ${p(e(saludo))}
      ${p('Hace un tiempo probaste Alexia y, desde entonces, cambió mucho.')}
      ${p('Sabemos cómo es: entre todo el trabajo del día, sentarte a revisar números siempre queda para otro día.')}
      ${p('Por eso la rehicimos para que te lo ponga mucho más fácil. Para que abras el celular y sepas cómo va tu restaurante, dónde se te va el dinero y qué merece tu atención hoy, sin tener que ser experto en finanzas.')}
      ${p('Queremos que lo veas con tus propios números, así que <b style="color:#0E4A42">te activamos 14 días más con el plan Pro</b>. Tu cuenta ya está lista: solo tienes que entrar.')}
      ${p('Te dejamos un video corto para que veas cómo es ahora:', 20)}
    </td></tr>

    <tr><td class="pad" style="padding:0 40px">
      <a href="${e(video)}" style="display:block;text-decoration:none">
        <img src="${e(miniatura)}" width="520" alt="Ver el video: así es Alexia ahora (1:24)" style="display:block;width:100%;max-width:520px;height:auto;border:0;border-radius:12px">
      </a>
    </td></tr>

    <tr><td class="pad" style="padding:26px 40px 0 40px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#E6F0EC;border-radius:12px">
        <tr><td style="padding:18px 20px;font-size:15px;line-height:1.6;color:#0E4A42">
          <b>Y una buena noticia:</b> ${noticia}
        </td></tr>
      </table>
    </td></tr>

    <tr><td class="pad" style="padding:22px 40px 0 40px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif">
      <p style="margin:0;font-size:16px;line-height:1.6;color:#37352F">¿Prefieres que lo veamos juntos? Contesta este correo y hacemos una sesión para dejar tu cuenta lista.</p>
    </td></tr>

    <tr><td class="pad" align="left" style="padding:24px 40px 0 40px">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
        <td style="border-radius:999px;background:#14776A"><a href="${e(app)}" style="display:inline-block;padding:14px 28px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif;font-size:16px;font-weight:700;color:#FFFFFF;text-decoration:none;border-radius:999px">Entrar a Alexia</a></td>
      </tr></table>
    </td></tr>

    <tr><td class="pad" style="padding:28px 40px 34px 40px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif">
      <p style="margin:0;font-size:15px;line-height:1.6;color:#37352F">El equipo de Alexia</p>
    </td></tr>
  </table>

  <table role="presentation" class="wrap" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px">
    <tr><td class="pad" align="center" style="padding:18px 40px;font-family:'Plus Jakarta Sans',Helvetica,Arial,sans-serif;font-size:12px;line-height:1.6;color:#91908C">
      Recibes este correo porque creaste una cuenta en Alexia Rest.<br>
      Alexia Rest · Polar Consulting Group 33 SL · ${pie}
    </td></tr>
  </table>

</td></tr>
</table>
</body>
</html>
`;

  const texto = [
    'Te activamos 14 días más en Alexia.',
    saludo,
    'Hace un tiempo probaste Alexia y, desde entonces, cambió mucho.',
    'Sabemos cómo es: entre todo el trabajo del día, sentarte a revisar números siempre queda para otro día.',
    'Por eso la rehicimos para que te lo ponga mucho más fácil. Para que abras el celular y sepas cómo va tu restaurante, dónde se te va el dinero y qué merece tu atención hoy, sin tener que ser experto en finanzas.',
    'Queremos que lo veas con tus propios números, así que te activamos 14 días más con el plan Pro. Tu cuenta ya está lista: solo tienes que entrar.',
    `Te dejamos un video corto para que veas cómo es ahora: ${video}`,
    `Y una buena noticia: ${noticia}`,
    '¿Prefieres que lo veamos juntos? Contesta este correo y hacemos una sesión para dejar tu cuenta lista.',
    `Entrar a Alexia: ${app}`,
    'El equipo de Alexia',
    `—\nRecibes este correo porque creaste una cuenta en Alexia Rest.\nAlexia Rest · Polar Consulting Group 33 SL · ${opciones.promocional && opciones.bajaUrl ? `Darme de baja: ${opciones.bajaUrl}` : `Gestionar correos: ${gestionar}`}`,
  ].join('\n\n');

  return { asunto: ASUNTO_REACTIVACION, preheader: PREHEADER_REACTIVACION, html, texto };
}
